import React, { useMemo, useState, useRef, useEffect } from 'react';
import { X, Trash2, Upload, Plus } from 'lucide-react';
import SavedBadge, { useSavedBadge } from './SavedBadge';
import SupplierAutocomplete from './SupplierAutocomplete';
import BrandAutocomplete from './BrandAutocomplete';

const ProductFormModal = ({ product, onClose, onSave, categories = [], allProducts = [], suppliers = [] }) => {
  const [formData, setFormData] = useState({
    label: product?.label || '',
    price: product?.price || 0,
    purchasePrice: product?.purchasePrice || 0,
    margin: product?.margin || 1.3,
    brand: product?.brand || '',
    category: product?.category || '',
    ref: product?.ref || '',
    summary: product?.summary || '',
    images: Array.isArray(product?.images) ? product.images : (typeof product?.images === 'string' ? [product.images] : []),
    description: product?.description || '',
    videoUrl: product?.videoUrl || '',
    manuals: Array.isArray(product?.manuals) ? product.manuals : [],
    plans: Array.isArray(product?.plans) ? product.plans : [],
    datasheets: Array.isArray(product?.datasheets) ? product.datasheets : [],
    warranty: product?.warranty || '',
    warrantyExtension: product?.warrantyExtension || { enabled: false, duration: '', price: 0 },
    stock: product?.stock || 0,
    options: Array.isArray(product?.options) ? product.options : [],
    variants: Array.isArray(product?.variants) ? product.variants : [],
    isMatrix: product?.isMatrix || false,
    isEndOfSeries: product?.isEndOfSeries || false,
    isFlashSale: product?.isFlashSale || false,
    flashSaleDiscount: product?.flashSaleDiscount || 0,
    flashSaleStartDate: product?.flashSaleStartDate || '',
    flashSaleEndDate: product?.flashSaleEndDate || '',
    reminderEligible: product?.reminderEligible || false,
    thresholds: product?.thresholds || { critical: 5, low: 15 },
    suppliers: Array.isArray(product?.suppliers) ? product.suppliers : [{ 
      name: '', 
      url: '', 
      lastPrice: 0,
      country: '',
      deliveryTime: '',
      address: '',
      paymentMethod: '',
      contactName: '',
      phone: '',
      email: ''
    }]
  });

  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const [savedLocal, triggerSavedLocal] = useSavedBadge(1800);
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const newCategoryInputRef = useRef(null);
  
  // États locaux pour les champs de prix (permettre la saisie de virgule/point)
  const [priceInput, setPriceInput] = useState(String(product?.price || ''));
  const [purchasePriceInput, setPurchasePriceInput] = useState(String(product?.purchasePrice || ''));
  const [marginInput, setMarginInput] = useState(String(product?.margin || ''));

  const categoryOptions = useMemo(() => {
    const set = new Set(
      (Array.isArray(categories) ? categories : [])
        .map((c) => String(c || '').trim())
        .filter(Boolean)
    );

    const current = String(product?.category || '').trim();
    if (current) set.add(current);

    return Array.from(set).sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
  }, [categories, product?.category]);

  useEffect(() => {
    // Si la catégorie actuelle n'existe pas dans la liste (ou est vide), on bascule sur "nouvelle catégorie".
    const current = String(formData.category || '').trim();
    if (!current) {
      setIsCreatingCategory(false);
      return;
    }
    const exists = categoryOptions.some((c) => c.toLowerCase() === current.toLowerCase());
    setIsCreatingCategory(!exists);
  }, [categoryOptions, formData.category]);

  useEffect(() => {
    if (isCreatingCategory) {
      // Focus auto quand l'utilisateur choisit "nouvelle catégorie".
      requestAnimationFrame(() => newCategoryInputRef.current?.focus());
    }
  }, [isCreatingCategory]);

  const handleFiles = (files) => {
    Array.from(files).forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFormData(prev => ({
            ...prev,
            images: [...prev.images, e.target.result]
          }));
        };
        reader.readAsDataURL(file);
      }
    });
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = () => {
    setIsDragging(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const generateVariants = () => {
    if (formData.options.length === 0) return;

    // Helper to get cartesian product of options
    const cartesian = (args) => {
      let r = [], max = args.length - 1;
      function helper(arr, i) {
        for (let j = 0, l = args[i].values.length; j < l; j++) {
          let a = arr.slice();
          a.push({ name: args[i].name, value: args[i].values[j].label });
          if (i === max) r.push(a);
          else helper(a, i + 1);
        }
      }
      helper([], 0);
      return r;
    };

    const combinations = cartesian(formData.options);
    const newVariants = combinations.map(combo => {
      // Try to find existing variant to preserve stock/price
      const existing = formData.variants.find(v => 
        combo.every(c => v.options[c.name] === c.value)
      );

      if (existing) return existing;

      const variantOptions = {};
      combo.forEach(c => variantOptions[c.name] = c.value);
      
      return {
        id: Math.random().toString(36).substr(2, 9),
        options: variantOptions,
        stock: 0,
        priceMod: 0,
        purchasePrice: 0,
        margin: 1.3
      };
    });

    setFormData(prev => ({ ...prev, variants: newVariants }));
  };

  const updateVariant = (idx, field, val) => {
    const newVariants = [...formData.variants];
    newVariants[idx][field] = val;
    setFormData(prev => ({ ...prev, variants: newVariants }));
  };

  const removeVariant = (idx) => {
    setFormData(prev => ({ ...prev, variants: prev.variants.filter((_, i) => i !== idx) }));
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const generateAutoReference = () => {
    // Génère une référence basée sur la désignation
    const label = (formData.label || 'PROD').trim();
    
    // Nettoie la désignation : majuscules, sans espaces/caractères spéciaux
    const baseRef = label
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Supprime les accents
      .replace(/[^A-Z0-9]/g, '-') // Remplace caractères spéciaux par -
      .replace(/-+/g, '-') // Fusionne les tirets multiples
      .replace(/^-|-$/g, '') // Supprime tirets début/fin
      .substring(0, 20); // Limite à 20 caractères

    // Trouve le prochain numéro disponible
    const existingRefs = (Array.isArray(allProducts) ? allProducts : [])
      .map(p => p.ref)
      .filter(ref => ref && ref.startsWith(baseRef));

    let counter = 1;
    let newRef = `${baseRef}-${String(counter).padStart(3, '0')}`;
    
    while (existingRefs.includes(newRef)) {
      counter++;
      newRef = `${baseRef}-${String(counter).padStart(3, '0')}`;
    }

    handleChange('ref', newRef);
  };

  const addOption = () => {
    setFormData(prev => ({
      ...prev,
      options: [...prev.options, { name: '', values: [{ label: '', priceMod: 0, stock: 0, purchasePrice: 0, margin: 1.3 }] }]
    }));
  };

  const updateOptionName = (idx, name) => {
    const newOpts = [...formData.options];
    newOpts[idx].name = name;
    setFormData(prev => ({ ...prev, options: newOpts }));
  };

  const addOptionValue = (optIdx) => {
    const newOpts = [...formData.options];
    newOpts[optIdx].values.push({ label: '', priceMod: 0, stock: 0, purchasePrice: 0, margin: 1.3 });
    setFormData(prev => ({ ...prev, options: newOpts }));
  };

  const updateOptionValue = (optIdx, valIdx, field, val) => {
    const newOpts = [...formData.options];
    newOpts[optIdx].values[valIdx][field] = val;
    setFormData(prev => ({ ...prev, options: newOpts }));
  };

  const removeOption = (idx) => {
    setFormData(prev => ({ ...prev, options: prev.options.filter((_, i) => i !== idx) }));
  };

  const removeOptionValue = (optIdx, valIdx) => {
    const newOpts = [...formData.options];
    newOpts[optIdx].values = newOpts[optIdx].values.filter((_, i) => i !== valIdx);
    setFormData(prev => ({ ...prev, options: newOpts }));
  };

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 w-full max-w-2xl rounded-[32px] shadow-2xl flex flex-col max-h-[90vh]">
        <div className="p-6 border-b dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-900">
          <h3 className="text-xl font-black dark:text-white">{product ? 'Modifier' : 'Ajouter'} un article</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full dark:text-white transition-colors"><X /></button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const category = String(formData.category || '').trim();
            if (!category) {
              if (isCreatingCategory) newCategoryInputRef.current?.focus();
              return;
            }
            onSave({
              ...formData,
              category
            });
            triggerSavedLocal();
          }}
          className="p-8 overflow-y-auto space-y-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1 text-left">
              <label className="text-[10px] font-black uppercase text-slate-400">Désignation</label>
              <input required className="w-full bg-slate-50 dark:bg-slate-800 p-3 rounded-xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500" value={formData.label} onChange={e => handleChange('label', e.target.value)} />
            </div>
            <div className="space-y-1 text-left">
              <label className="text-[10px] font-black uppercase text-slate-400">Référence</label>
              <div className="flex gap-2">
                <input required className="flex-1 bg-slate-50 dark:bg-slate-800 p-3 rounded-xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500" value={formData.ref} onChange={e => handleChange('ref', e.target.value)} />
                <button
                  type="button"
                  onClick={generateAutoReference}
                  className="px-4 py-3 bg-blue-50 dark:bg-blue-900/20 text-blue-600 rounded-xl font-bold hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-all flex items-center gap-2 text-xs"
                  title="Générer une référence automatique"
                >
                  🔄 Auto
                </button>
              </div>
            </div>
            <div className="space-y-1 text-left">
              <label className="text-[10px] font-black uppercase text-slate-400">Prix d'achat HT (€)</label>
              <input 
                type="text" 
                inputMode="decimal"
                className="w-full bg-slate-50 dark:bg-slate-800 p-3 rounded-xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500" 
                value={purchasePriceInput} 
                onChange={e => {
                  let value = e.target.value;
                  // Permettre virgule et point, chiffres uniquement
                  value = value.replace(/[^0-9.,]/g, '');
                  // Remplacer toutes les virgules par des points pour la conversion
                  const numValue = value.replace(/,/g, '.');
                  
                  setPurchasePriceInput(value);
                  
                  const newPurchase = parseFloat(numValue) || 0;
                  const calculatedPrice = Math.round(newPurchase * formData.margin * 100) / 100;
                  handleChange('purchasePrice', newPurchase);
                  handleChange('price', calculatedPrice);
                  setPriceInput(String(calculatedPrice));
                }} 
                onBlur={e => {
                  const num = parseFloat(e.target.value.replace(/,/g, '.')) || 0;
                  setPurchasePriceInput(String(num));
                  handleChange('purchasePrice', num);
                }}
                placeholder="Ex: 125,50 ou 125.50"
              />
            </div>
            <div className="space-y-1 text-left">
              <label className="text-[10px] font-black uppercase text-slate-400">Marge</label>
              <input 
                type="text" 
                inputMode="decimal"
                className="w-full bg-slate-50 dark:bg-slate-800 p-3 rounded-xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500" 
                value={marginInput} 
                onChange={e => {
                  let value = e.target.value;
                  value = value.replace(/[^0-9.,]/g, '');
                  const numValue = value.replace(/,/g, '.');
                  
                  setMarginInput(value);
                  
                  const newMargin = parseFloat(numValue) || 1;
                  const calculatedPrice = Math.round(formData.purchasePrice * newMargin * 100) / 100;
                  handleChange('margin', newMargin);
                  handleChange('price', calculatedPrice);
                  setPriceInput(String(calculatedPrice));
                }} 
                onBlur={e => {
                  const num = parseFloat(e.target.value.replace(/,/g, '.')) || 1;
                  setMarginInput(String(num));
                  handleChange('margin', num);
                }}
                placeholder="Ex: 1,3 ou 1.3"
              />
            </div>
            <div className="space-y-1 text-left">
              <label className="text-[10px] font-black uppercase text-slate-400">Prix de vente HT (€)</label>
              <input 
                type="text" 
                inputMode="decimal"
                className="w-full bg-emerald-50 dark:bg-emerald-900/20 p-3 rounded-xl dark:text-white outline-none focus:ring-2 focus:ring-emerald-500 font-bold" 
                value={priceInput} 
                onChange={e => {
                  let value = e.target.value;
                  value = value.replace(/[^0-9.,]/g, '');
                  const numValue = value.replace(/,/g, '.');
                  
                  setPriceInput(value);
                  handleChange('price', parseFloat(numValue) || 0);
                }} 
                onBlur={e => {
                  const num = parseFloat(e.target.value.replace(/,/g, '.')) || 0;
                  setPriceInput(String(num));
                  handleChange('price', num);
                }}
                placeholder="Ex: 1250,50 ou 1250.50"
              />
            </div>
            <div className="space-y-1 text-left">
              <label className="text-[10px] font-black uppercase text-slate-400">Catégorie</label>
              {categoryOptions.length > 0 ? (
                <div className="space-y-2">
                  <select
                    required
                    className="w-full bg-slate-50 dark:bg-slate-800 p-3 rounded-xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
                    value={isCreatingCategory ? '__new__' : (String(formData.category || '').trim() || '')}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '__new__') {
                        setIsCreatingCategory(true);
                        handleChange('category', '');
                        return;
                      }
                      setIsCreatingCategory(false);
                      handleChange('category', v);
                    }}
                  >
                    <option value="" disabled>Choisir une catégorie</option>
                    {categoryOptions.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                    <option value="__new__">+ Créer une nouvelle catégorie…</option>
                  </select>

                  {isCreatingCategory && (
                    <input
                      ref={newCategoryInputRef}
                      required
                      className="w-full bg-slate-50 dark:bg-slate-800 p-3 rounded-xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Nom de la nouvelle catégorie"
                      value={formData.category}
                      onChange={(e) => handleChange('category', e.target.value)}
                    />
                  )}
                </div>
              ) : (
                <input
                  required
                  className="w-full bg-slate-50 dark:bg-slate-800 p-3 rounded-xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.category}
                  onChange={e => handleChange('category', e.target.value)}
                />
              )}
            </div>
            <div className="space-y-1 text-left">
              <label className="text-[10px] font-black uppercase text-slate-400">Marque</label>
              <BrandAutocomplete
                value={formData.brand}
                onChange={(brand) => handleChange('brand', brand)}
                allProducts={allProducts}
                placeholder="Tapez pour rechercher ou créer une marque..."
              />
            </div>
            <div className="space-y-1 text-left">
              <label className="text-[10px] font-black uppercase text-slate-400">Garantie</label>
              <input className="w-full bg-slate-50 dark:bg-slate-800 p-3 rounded-xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500" value={formData.warranty} onChange={e => handleChange('warranty', e.target.value)} placeholder="ex: 2 ans, 5 ans..." />
            </div>
            <div className="space-y-2 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border dark:border-slate-800">
              <div className="flex items-center gap-3 group">
                <button 
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, warrantyExtension: { ...prev.warrantyExtension, enabled: !prev.warrantyExtension.enabled } }))}
                  className={`w-10 h-5 rounded-full transition-all relative ${formData.warrantyExtension.enabled ? 'bg-blue-600' : 'bg-slate-300 dark:bg-slate-700'}`}
                  aria-pressed={formData.warrantyExtension.enabled}
                >
                  <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${formData.warrantyExtension.enabled ? 'left-6' : 'left-1'}`} />
                </button>
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">Extension de Garantie</span>
              </div>
              
              {formData.warrantyExtension.enabled && (
                <div className="grid grid-cols-2 gap-2 pt-2 animate-in fade-in slide-in-from-top-2">
                  <div className="space-y-1">
                    <label className="text-[9px] font-black uppercase text-slate-400">Durée</label>
                    <input 
                      className="w-full bg-white dark:bg-slate-900 p-2 rounded-lg dark:text-white outline-none border dark:border-slate-700 text-xs" 
                      value={formData.warrantyExtension.duration} 
                      onChange={e => setFormData(prev => ({ ...prev, warrantyExtension: { ...prev.warrantyExtension, duration: e.target.value } }))}
                      placeholder="ex: +3 ans"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[9px] font-black uppercase text-slate-400">Prix (€ HT)</label>
                    <input 
                      type="number"
                      className="w-full bg-white dark:bg-slate-900 p-2 rounded-lg dark:text-white outline-none border dark:border-slate-700 text-xs" 
                      value={formData.warrantyExtension.price} 
                      onChange={e => setFormData(prev => ({ ...prev, warrantyExtension: { ...prev.warrantyExtension, price: parseFloat(e.target.value) || 0 } }))}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-1 text-left">
              <label className="text-[10px] font-black uppercase text-slate-400">Stock Initial</label>
              <input required type="number" className="w-full bg-slate-50 dark:bg-slate-800 p-3 rounded-xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500" value={formData.stock} onChange={e => handleChange('stock', parseInt(e.target.value) || 0)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1 text-left">
                <label className="text-[10px] font-black uppercase text-red-500">Seuil Critique</label>
                <input type="number" className="w-full bg-red-50 dark:bg-red-900/10 p-3 rounded-xl dark:text-white outline-none border border-red-100 dark:border-red-900/30" value={formData.thresholds.critical} onChange={e => setFormData(prev => ({ ...prev, thresholds: { ...prev.thresholds, critical: parseInt(e.target.value) || 0 } }))} />
              </div>
              <div className="space-y-1 text-left">
                <label className="text-[10px] font-black uppercase text-amber-500">Seuil Faible</label>
                <input type="number" className="w-full bg-amber-50 dark:bg-amber-900/10 p-3 rounded-xl dark:text-white outline-none border border-amber-100 dark:border-amber-900/30" value={formData.thresholds.low} onChange={e => setFormData(prev => ({ ...prev, thresholds: { ...prev.thresholds, low: parseInt(e.target.value) || 0 } }))} />
              </div>
            </div>
            <div className="flex flex-col gap-4 pb-2">
              <div className="flex items-center gap-3 group">
                <button 
                  type="button"
                  onClick={() => handleChange('isMatrix', !formData.isMatrix)}
                  className={`w-10 h-5 rounded-full transition-all relative ${formData.isMatrix ? 'bg-purple-600' : 'bg-slate-300 dark:bg-slate-700'}`}
                  aria-pressed={formData.isMatrix}
                >
                  <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${formData.isMatrix ? 'left-6' : 'left-1'}`} />
                </button>
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">Mode Matrice (Inox/Diam/Long)</span>
              </div>

              <div className="flex items-center gap-3 group">
                <button 
                  type="button"
                  onClick={() => handleChange('isEndOfSeries', !formData.isEndOfSeries)}
                  className={`w-10 h-5 rounded-full transition-all relative ${formData.isEndOfSeries ? 'bg-amber-500' : 'bg-slate-300 dark:bg-slate-700'}`}
                  aria-pressed={formData.isEndOfSeries}
                >
                  <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${formData.isEndOfSeries ? 'left-6' : 'left-1'}`} />
                </button>
                <span className="text-[10px] font-black uppercase tracking-wider text-amber-600">Fin de série (Pas de réappro)</span>
              </div>

              <div className="flex items-center gap-3 group">
                <button 
                  type="button"
                  onClick={() => handleChange('reminderEligible', !formData.reminderEligible)}
                  className={`w-10 h-5 rounded-full transition-all relative ${formData.reminderEligible ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-700'}`}
                  aria-pressed={formData.reminderEligible}
                >
                  <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${formData.reminderEligible ? 'left-6' : 'left-1'}`} />
                </button>
                <span className="text-[10px] font-black uppercase tracking-wider text-emerald-600">Éligible au rappel de renouvellement</span>
              </div>

              <div className="space-y-3 p-4 bg-amber-50 dark:bg-amber-900/10 rounded-2xl border border-amber-100 dark:border-amber-900/30">
                <div className="flex items-center gap-3 group">
                  <button 
                    type="button"
                    onClick={() => handleChange('isFlashSale', !formData.isFlashSale)}
                    className={`w-10 h-5 rounded-full transition-all relative ${formData.isFlashSale ? 'bg-amber-500' : 'bg-slate-300 dark:bg-slate-700'}`}
                    aria-pressed={formData.isFlashSale}
                  >
                    <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${formData.isFlashSale ? 'left-6' : 'left-1'}`} />
                  </button>
                  <span className="text-[10px] font-black uppercase tracking-wider text-amber-600">Vente Flash Active</span>
                </div>
                
                {formData.isFlashSale && (
                  <div className="space-y-3 animate-in fade-in slide-in-from-top-2">
                    <div className="space-y-1">
                      <label className="text-[9px] font-black uppercase text-amber-500">Remise (%)</label>
                      <div className="relative">
                        <input 
                          type="number"
                          min="0"
                          max="100"
                          className="w-full bg-white dark:bg-slate-900 p-2 rounded-lg dark:text-white outline-none border dark:border-slate-700 text-xs font-bold" 
                          value={formData.flashSaleDiscount} 
                          onChange={e => handleChange('flashSaleDiscount', parseFloat(e.target.value) || 0)}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-black text-amber-500">%</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <div className="flex justify-between items-center">
                          <label className="text-[9px] font-black uppercase text-amber-500">Début (Optionnel)</label>
                          {(formData.flashSaleStartDate || formData.flashSaleEndDate) && (
                            <button 
                              type="button"
                              onClick={() => {
                                handleChange('flashSaleStartDate', '');
                                handleChange('flashSaleEndDate', '');
                              }}
                              className="text-[8px] font-black uppercase text-slate-400 hover:text-red-500 transition-colors"
                            >
                              Vider
                            </button>
                          )}
                        </div>
                        <input 
                          type="datetime-local"
                          className="w-full bg-white dark:bg-slate-900 p-2 rounded-lg dark:text-white outline-none border dark:border-slate-700 text-[10px] font-bold" 
                          value={formData.flashSaleStartDate} 
                          onChange={e => handleChange('flashSaleStartDate', e.target.value)}
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[9px] font-black uppercase text-amber-500">Fin (Optionnel)</label>
                        <input 
                          type="datetime-local"
                          className="w-full bg-white dark:bg-slate-900 p-2 rounded-lg dark:text-white outline-none border dark:border-slate-700 text-[10px] font-bold" 
                          value={formData.flashSaleEndDate} 
                          onChange={e => handleChange('flashSaleEndDate', e.target.value)}
                        />
                      </div>
                    </div>

                    {formData.price > 0 && (
                      <p className="text-[9px] font-bold text-slate-400 mt-1 italic">
                        Prix final : <span className="text-amber-600">{(formData.price * (1 - formData.flashSaleDiscount / 100)).toFixed(2)} € HT</span>
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Section Fournisseurs */}
          <div className="space-y-4 border-t dark:border-slate-800 pt-6">
            <div className="flex justify-between items-center">
              <label className="text-[10px] font-black uppercase text-slate-400">Informations Fournisseurs & Réapprovisionnement</label>
              <button 
                type="button" 
                onClick={() => setFormData(prev => ({ ...prev, suppliers: [...prev.suppliers, { 
                  name: '', url: '', lastPrice: 0, country: '', deliveryTime: '', address: '', paymentMethod: '', contactName: '', phone: '', email: '' 
                }] }))}
                className="text-blue-600 font-black text-[10px] uppercase tracking-widest hover:underline"
              >
                + Ajouter un fournisseur
              </button>
            </div>
            <div className="space-y-3">
              {formData.suppliers.map((sup, sIdx) => (
                <div key={sIdx} className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl space-y-3 relative">
                  <button 
                    type="button" 
                    onClick={() => setFormData(prev => ({ ...prev, suppliers: prev.suppliers.filter((_, i) => i !== sIdx) }))}
                    className="absolute top-2 right-2 text-red-500 p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-[9px] font-black uppercase text-slate-400">Nom du Fournisseur</label>
                      <SupplierAutocomplete
                        suppliers={suppliers}
                        value={sup}
                        onChange={(selectedSupplier) => {
                          const newSups = [...formData.suppliers];
                          // Remplir automatiquement tous les champs
                          newSups[sIdx] = {
                            ...newSups[sIdx],
                            ...selectedSupplier
                          };
                          setFormData(prev => ({ ...prev, suppliers: newSups }));
                        }}
                        onClear={() => {
                          const newSups = [...formData.suppliers];
                          newSups[sIdx] = {
                            name: '',
                            url: '',
                            lastPrice: 0,
                            country: '',
                            deliveryTime: '',
                            address: '',
                            paymentMethod: '',
                            contactName: '',
                            phone: '',
                            email: ''
                          };
                          setFormData(prev => ({ ...prev, suppliers: newSups }));
                        }}
                        placeholder="Tapez pour rechercher un fournisseur..."
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[9px] font-black uppercase text-slate-400">Prix dernier achat (€ HT)</label>
                      <input 
                        type="number"
                        step="0.01"
                        className="w-full bg-white dark:bg-slate-900 p-2 rounded-lg dark:text-white outline-none border dark:border-slate-700 text-xs" 
                        value={sup.lastPrice} 
                        onChange={e => {
                          const newSups = [...formData.suppliers];
                          newSups[sIdx].lastPrice = parseFloat(e.target.value) || 0;
                          setFormData(prev => ({ ...prev, suppliers: newSups }));
                        }}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <label className="text-[9px] font-black uppercase text-slate-400">Pays</label>
                      <input 
                        className="w-full bg-white dark:bg-slate-900 p-2 rounded-lg dark:text-white outline-none border dark:border-slate-700 text-xs" 
                        value={sup.country || ''} 
                        onChange={e => {
                          const newSups = [...formData.suppliers];
                          newSups[sIdx].country = e.target.value;
                          setFormData(prev => ({ ...prev, suppliers: newSups }));
                        }}
                        placeholder="France, Allemagne..."
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[9px] font-black uppercase text-slate-400">Délai Livraison</label>
                      <input 
                        className="w-full bg-white dark:bg-slate-900 p-2 rounded-lg dark:text-white outline-none border dark:border-slate-700 text-xs" 
                        value={sup.deliveryTime || ''} 
                        onChange={e => {
                          const newSups = [...formData.suppliers];
                          newSups[sIdx].deliveryTime = e.target.value;
                          setFormData(prev => ({ ...prev, suppliers: newSups }));
                        }}
                        placeholder="ex: 48h, 1 semaine..."
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[9px] font-black uppercase text-slate-400">Mode Paiement</label>
                      <input 
                        className="w-full bg-white dark:bg-slate-900 p-2 rounded-lg dark:text-white outline-none border dark:border-slate-700 text-xs" 
                        value={sup.paymentMethod || ''} 
                        onChange={e => {
                          const newSups = [...formData.suppliers];
                          newSups[sIdx].paymentMethod = e.target.value;
                          setFormData(prev => ({ ...prev, suppliers: newSups }));
                        }}
                        placeholder="Virement, 30j..."
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <label className="text-[9px] font-black uppercase text-slate-400">Contact</label>
                      <input 
                        className="w-full bg-white dark:bg-slate-900 p-2 rounded-lg dark:text-white outline-none border dark:border-slate-700 text-xs" 
                        value={sup.contactName || ''} 
                        onChange={e => {
                          const newSups = [...formData.suppliers];
                          newSups[sIdx].contactName = e.target.value;
                          setFormData(prev => ({ ...prev, suppliers: newSups }));
                        }}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[9px] font-black uppercase text-slate-400">Téléphone</label>
                      <input 
                        className="w-full bg-white dark:bg-slate-900 p-2 rounded-lg dark:text-white outline-none border dark:border-slate-700 text-xs" 
                        value={sup.phone || ''} 
                        onChange={e => {
                          const newSups = [...formData.suppliers];
                          newSups[sIdx].phone = e.target.value;
                          setFormData(prev => ({ ...prev, suppliers: newSups }));
                        }}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[9px] font-black uppercase text-slate-400">Email</label>
                      <input 
                        className="w-full bg-white dark:bg-slate-900 p-2 rounded-lg dark:text-white outline-none border dark:border-slate-700 text-xs" 
                        value={sup.email || ''} 
                        onChange={e => {
                          const newSups = [...formData.suppliers];
                          newSups[sIdx].email = e.target.value;
                          setFormData(prev => ({ ...prev, suppliers: newSups }));
                        }}
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[9px] font-black uppercase text-slate-400">Adresse</label>
                    <input 
                      className="w-full bg-white dark:bg-slate-900 p-2 rounded-lg dark:text-white outline-none border dark:border-slate-700 text-xs" 
                      value={sup.address || ''} 
                      onChange={e => {
                        const newSups = [...formData.suppliers];
                        newSups[sIdx].address = e.target.value;
                        setFormData(prev => ({ ...prev, suppliers: newSups }));
                      }}
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[9px] font-black uppercase text-slate-400">Lien vers l'article (Site Distributeur)</label>
                    <input 
                      className="w-full bg-white dark:bg-slate-900 p-2 rounded-lg dark:text-white outline-none border dark:border-slate-700 text-xs" 
                      value={sup.url} 
                      onChange={e => {
                        const newSups = [...formData.suppliers];
                        newSups[sIdx].url = e.target.value;
                        setFormData(prev => ({ ...prev, suppliers: newSups }));
                      }}
                      placeholder="https://www.fournisseur.com/produit/..."
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3 text-left">
            <label className="text-[10px] font-black uppercase text-slate-400">Images du produit</label>
            
            {/* Drop Zone */}
            <div 
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-[24px] p-8 transition-all cursor-pointer flex flex-col items-center justify-center gap-3 ${
                isDragging 
                  ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20' 
                  : 'border-slate-200 dark:border-slate-800 hover:border-blue-400 dark:hover:border-blue-800 bg-slate-50/50 dark:bg-slate-800/30'
              }`}
            >
              <input 
                type="file" 
                ref={fileInputRef}
                onChange={(e) => handleFiles(e.target.files)}
                multiple 
                accept="image/*"
                className="hidden" 
              />
              <div className="p-4 bg-white dark:bg-slate-900 rounded-2xl shadow-sm">
                <Upload className={`w-6 h-6 ${isDragging ? 'text-blue-600' : 'text-slate-400'}`} />
              </div>
              <div className="text-center">
                <p className="text-xs font-black uppercase tracking-widest text-slate-600 dark:text-slate-300">Glissez vos images ici</p>
                <p className="text-[10px] text-slate-400 font-bold mt-1">ou cliquez pour parcourir vos fichiers</p>
              </div>
            </div>

            {/* Image Gallery */}
            {formData.images.length > 0 && (
              <div className="grid grid-cols-4 gap-3 mt-4">
                {formData.images.map((img, idx) => (
                  <div key={idx} className="relative group aspect-square rounded-xl overflow-hidden border dark:border-slate-800 bg-white dark:bg-slate-900">
                    <img src={img} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button 
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          const newImgs = formData.images.filter((_, i) => i !== idx);
                          handleChange('images', newImgs);
                        }}
                        className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
                <button 
                  type="button"
                  onClick={() => {
                    const url = prompt("Entrez l'URL de l'image :");
                    if (url) handleChange('images', [...formData.images, url]);
                  }}
                  className="aspect-square rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-800 flex flex-col items-center justify-center gap-1 text-slate-400 hover:text-blue-600 hover:border-blue-400 transition-all"
                >
                  <Plus className="w-4 h-4" />
                  <span className="text-[8px] font-black uppercase">URL</span>
                </button>
              </div>
            )}
          </div>
          <div className="space-y-1 text-left">
            <label className="text-[10px] font-black uppercase text-slate-400">Description</label>
            <textarea rows="3" className="w-full bg-slate-50 dark:bg-slate-800 p-3 rounded-xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500" value={formData.description} onChange={e => handleChange('description', e.target.value)} />
          </div>

          {/* Ressources & Médias */}
          <div className="space-y-6 border-t dark:border-slate-800 pt-6">
            <h3 className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Ressources & Médias</h3>
            
            <div className="space-y-1 text-left">
              <label className="text-[10px] font-black uppercase text-slate-400">URL Vidéo (YouTube/Vimeo)</label>
              <input className="w-full bg-slate-50 dark:bg-slate-800 p-3 rounded-xl dark:text-white outline-none focus:ring-2 focus:ring-blue-500" value={formData.videoUrl} onChange={e => handleChange('videoUrl', e.target.value)} placeholder="https://www.youtube.com/embed/..." />
            </div>

            {/* Manuels */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <label className="text-[10px] font-black uppercase text-slate-400">Manuels d'utilisation</label>
                <button type="button" onClick={() => setFormData(prev => ({ ...prev, manuals: [...prev.manuals, { label: '', url: '' }] }))} className="text-blue-600 font-black text-[10px] uppercase tracking-widest">+ Ajouter</button>
              </div>
              {formData.manuals.map((item, idx) => (
                <div key={idx} className="flex gap-2">
                  <input className="flex-1 bg-slate-50 dark:bg-slate-800 p-2 rounded-lg dark:text-white text-xs outline-none" value={item.label} onChange={e => {
                    const newItems = [...formData.manuals];
                    newItems[idx].label = e.target.value;
                    setFormData(prev => ({ ...prev, manuals: newItems }));
                  }} placeholder="Nom du manuel" />
                  <input className="flex-[2] bg-slate-50 dark:bg-slate-800 p-2 rounded-lg dark:text-white text-xs outline-none" value={item.url} onChange={e => {
                    const newItems = [...formData.manuals];
                    newItems[idx].url = e.target.value;
                    setFormData(prev => ({ ...prev, manuals: newItems }));
                  }} placeholder="URL du PDF" />
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, manuals: prev.manuals.filter((_, i) => i !== idx) }))} className="text-red-500 p-2"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>

            {/* Plans */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <label className="text-[10px] font-black uppercase text-slate-400">Plans & Schémas</label>
                <button type="button" onClick={() => setFormData(prev => ({ ...prev, plans: [...prev.plans, { label: '', url: '' }] }))} className="text-blue-600 font-black text-[10px] uppercase tracking-widest">+ Ajouter</button>
              </div>
              {formData.plans.map((item, idx) => (
                <div key={idx} className="flex gap-2">
                  <input className="flex-1 bg-slate-50 dark:bg-slate-800 p-2 rounded-lg dark:text-white text-xs outline-none" value={item.label} onChange={e => {
                    const newItems = [...formData.plans];
                    newItems[idx].label = e.target.value;
                    setFormData(prev => ({ ...prev, plans: newItems }));
                  }} placeholder="Nom du plan" />
                  <input className="flex-[2] bg-slate-50 dark:bg-slate-800 p-2 rounded-lg dark:text-white text-xs outline-none" value={item.url} onChange={e => {
                    const newItems = [...formData.plans];
                    newItems[idx].url = e.target.value;
                    setFormData(prev => ({ ...prev, plans: newItems }));
                  }} placeholder="URL du fichier" />
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, plans: prev.plans.filter((_, i) => i !== idx) }))} className="text-red-500 p-2"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>

            {/* Datasheets */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <label className="text-[10px] font-black uppercase text-slate-400">Datasheets / Fiches Techniques</label>
                <button type="button" onClick={() => setFormData(prev => ({ ...prev, datasheets: [...prev.datasheets, { label: '', url: '' }] }))} className="text-blue-600 font-black text-[10px] uppercase tracking-widest">+ Ajouter</button>
              </div>
              {formData.datasheets.map((item, idx) => (
                <div key={idx} className="flex gap-2">
                  <input className="flex-1 bg-slate-50 dark:bg-slate-800 p-2 rounded-lg dark:text-white text-xs outline-none" value={item.label} onChange={e => {
                    const newItems = [...formData.datasheets];
                    newItems[idx].label = e.target.value;
                    setFormData(prev => ({ ...prev, datasheets: newItems }));
                  }} placeholder="Nom de la fiche" />
                  <input className="flex-[2] bg-slate-50 dark:bg-slate-800 p-2 rounded-lg dark:text-white text-xs outline-none" value={item.url} onChange={e => {
                    const newItems = [...formData.datasheets];
                    newItems[idx].url = e.target.value;
                    setFormData(prev => ({ ...prev, datasheets: newItems }));
                  }} placeholder="URL du PDF" />
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, datasheets: prev.datasheets.filter((_, i) => i !== idx) }))} className="text-red-500 p-2"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Gestion des Options / Variantes */}
          <div className="space-y-4 border-t dark:border-slate-800 pt-6">
            <div className="flex justify-between items-center">
              <label className="text-[10px] font-black uppercase text-slate-400">Variantes & Options (ex: Longueur, Matériau)</label>
              <button type="button" onClick={addOption} className="text-blue-600 font-black text-[10px] uppercase tracking-widest hover:underline">+ Ajouter une option</button>
            </div>
            
            <div className="space-y-6">
              {formData.options.map((opt, optIdx) => (
                <div key={optIdx} className="bg-slate-50 dark:bg-slate-800/50 p-6 rounded-2xl space-y-4 relative">
                  <button type="button" onClick={() => removeOption(optIdx)} className="absolute top-4 right-4 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 p-2 rounded-lg transition-all"><Trash2 className="w-4 h-4" /></button>
                  
                  <div className="space-y-1 text-left pr-10">
                    <label className="text-[10px] font-black uppercase text-slate-400">Nom de l'option (ex: Longueur)</label>
                    <input className="w-full bg-white dark:bg-slate-900 p-3 rounded-xl dark:text-white outline-none border dark:border-slate-700" value={opt.name} onChange={e => updateOptionName(optIdx, e.target.value)} placeholder="ex: Matériau" />
                  </div>

                  <div className="space-y-3">
                    <label className="text-[10px] font-black uppercase text-slate-400">Valeurs & Modificateurs de prix (Base)</label>
                    {opt.values.map((val, valIdx) => (
                      <div key={valIdx} className="flex gap-2 items-center">
                        <input 
                          className="flex-1 bg-white dark:bg-slate-900 p-2 rounded-lg dark:text-white outline-none border dark:border-slate-700 text-sm" 
                          value={val.label} 
                          onChange={e => updateOptionValue(optIdx, valIdx, 'label', e.target.value)} 
                          placeholder="Valeur (ex: Inox)" 
                        />
                        <div className="relative w-20">
                          <input 
                            type="number" 
                            step="0.01" 
                            className="w-full bg-white dark:bg-slate-900 p-2 rounded-lg dark:text-white outline-none border dark:border-slate-700 text-xs" 
                            value={val.purchasePrice || ''} 
                            onChange={e => {
                              const newPurchase = parseFloat(e.target.value) || 0;
                              updateOptionValue(optIdx, valIdx, 'purchasePrice', newPurchase);
                              updateOptionValue(optIdx, valIdx, 'priceMod', newPurchase * (val.margin || 1.3));
                            }} 
                            placeholder="Achat" 
                            title="Prix d'achat HT"
                          />
                        </div>
                        <div className="relative w-16">
                          <input 
                            type="number" 
                            step="0.1" 
                            className="w-full bg-white dark:bg-slate-900 p-2 rounded-lg dark:text-white outline-none border dark:border-slate-700 text-xs" 
                            value={val.margin || ''} 
                            onChange={e => {
                              const newMargin = parseFloat(e.target.value) || 1;
                              updateOptionValue(optIdx, valIdx, 'margin', newMargin);
                              updateOptionValue(optIdx, valIdx, 'priceMod', (val.purchasePrice || 0) * newMargin);
                            }} 
                            placeholder="Marge" 
                            title="Marge multiplicatrice"
                          />
                        </div>
                        <div className="relative w-20">
                          <input 
                            type="number" 
                            step="0.01" 
                            className="w-full bg-emerald-50 dark:bg-emerald-900/20 p-2 rounded-lg dark:text-white outline-none border border-emerald-200 dark:border-emerald-800 text-xs font-bold" 
                            value={val.priceMod ? val.priceMod.toFixed(2) : ''} 
                            onChange={e => updateOptionValue(optIdx, valIdx, 'priceMod', parseFloat(e.target.value) || 0)} 
                            title="Prix de vente HT calculé"
                          />
                        </div>
                        <button type="button" onClick={() => removeOptionValue(optIdx, valIdx)} className="p-2 text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                      </div>
                    ))}
                    <button type="button" onClick={() => addOptionValue(optIdx)} className="text-blue-600 font-bold text-[10px] uppercase tracking-widest hover:underline">+ Ajouter une valeur</button>
                  </div>
                </div>
              ))}
            </div>

            {formData.options.length > 0 && (
              <div className="space-y-4 border-t dark:border-slate-800 pt-6">
                <div className="flex justify-between items-center">
                  <label className="text-[10px] font-black uppercase text-slate-400">Combinaisons de Variantes (Stock Réel)</label>
                  <button type="button" onClick={generateVariants} className="bg-blue-50 dark:bg-blue-900/20 text-blue-600 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-100 transition-all">
                    Générer les combinaisons
                  </button>
                </div>

                <div className="space-y-2 max-h-60 overflow-y-auto pr-2">
                  {formData.variants.map((v, vIdx) => (
                    <div key={v.id || vIdx} className="flex gap-2 items-center bg-slate-50 dark:bg-slate-800/30 p-3 rounded-xl border dark:border-slate-800">
                      <div className="flex gap-2 items-center flex-1">
                        <div className="relative w-20">
                          <input type="number" className="w-full bg-white dark:bg-slate-900 p-2 rounded-lg dark:text-white outline-none border dark:border-slate-700 text-xs" value={v.stock} onChange={e => updateVariant(vIdx, 'stock', parseInt(e.target.value) || 0)} placeholder="Stock" />
                        </div>
                        <div className="relative w-20">
                          <input 
                            type="number" 
                            step="0.01" 
                            className="w-full bg-white dark:bg-slate-900 p-2 rounded-lg dark:text-white outline-none border dark:border-slate-700 text-xs" 
                            value={v.purchasePrice || ''} 
                            onChange={e => {
                              const newPurchase = parseFloat(e.target.value) || 0;
                              updateVariant(vIdx, 'purchasePrice', newPurchase);
                              updateVariant(vIdx, 'priceMod', newPurchase * (v.margin || 1.8));
                            }} 
                            placeholder="Achat" 
                            title="Prix d'achat HT"
                          />
                        </div>
                        <div className="relative w-16">
                          <input 
                            type="number" 
                            step="0.1" 
                            className="w-full bg-white dark:bg-slate-900 p-2 rounded-lg dark:text-white outline-none border dark:border-slate-700 text-xs" 
                            value={v.margin || ''} 
                            onChange={e => {
                              const newMargin = parseFloat(e.target.value) || 1;
                              updateVariant(vIdx, 'margin', newMargin);
                              updateVariant(vIdx, 'priceMod', (v.purchasePrice || 0) * newMargin);
                            }} 
                            placeholder="x" 
                            title="Marge"
                          />
                        </div>
                        <div className="relative w-20">
                          <input type="number" step="0.01" className="w-full bg-emerald-50 dark:bg-emerald-900/20 p-2 rounded-lg dark:text-white outline-none border border-emerald-200 dark:border-emerald-800 text-xs font-bold" value={v.priceMod ? v.priceMod.toFixed(2) : ''} onChange={e => updateVariant(vIdx, 'priceMod', parseFloat(e.target.value) || 0)} placeholder="Prix" title="Prix de vente HT" />
                        </div>
                        <button type="button" onClick={() => removeVariant(vIdx)} className="p-2 text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button type="submit" className="w-full bg-blue-600 text-white py-4 rounded-xl font-black shadow-lg hover:bg-blue-700 transition-all">
              Enregistrer
            </button>
            <SavedBadge visible={savedLocal} />
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductFormModal;
